'use client'

import React, { useMemo, useRef } from 'react'
import { JSONDocNode, JSONNode } from 'src/lib/types/support'
import { extractTextFromADF } from 'src/lib/utils/support'

interface ComposableEditorPageProps {
  value: JSONDocNode
  handleChange?: (adf: JSONDocNode) => void
  readOnly?: boolean
}

export const filterMediaNodes = (content: Array<JSONNode | undefined> | undefined): JSONNode[] => {
  const safeContent = (content || []).filter((node): node is JSONNode => !!node && !node.type.startsWith('media'))

  return safeContent.map(node => {
    const filteredChildren = node.content ? filterMediaNodes(node.content) : undefined
    return {
      ...node,
      ...(filteredChildren ? { content: filteredChildren } : {})
    }
  })
}

export const ADFEditor: React.FC<ComposableEditorPageProps> = ({ value, handleChange, readOnly }) => {
  const editorContainerRef = useRef<HTMLDivElement>(null)

  // const universalPreset = useUniversalPreset({
  //   props: {
  //     allowUndoRedoButtons: false,
  //     allowFragmentMark: true,
  //     allowDate: false,
  //     allowHelpDialog: false,
  //     elementBrowser: {
  //       showModal: false,
  //       replacePlusMenu: !readOnly
  //     },
  //     allowTasksAndDecisions: false,
  //     __livePage: true,
  //     featureFlags: {
  //       'nested-expand-in-expand-ex': true,
  //       'table-drag-and-drop': true,
  //       'quick-insert': !readOnly
  //     },
  //     allowTemplatePlaceholders: true
  //   },

  //   initialPluginConfiguration: {
  //     tasksAndDecisionsPlugin: {
  //       hasEditPermission: !readOnly
  //     }
  //   }
  // })

  // const { preset, editorApi } = usePreset(
  //   () => universalPreset.add([editorViewModePlugin, { initialContentMode: readOnly ? 'live-view' : 'live-edit' }]),
  //   [universalPreset, readOnly]
  // )

  // useEffect(() => {
  //   if (!editorApi?.editorViewMode) return
  //   editorApi.core?.actions.execute(editorApi.editorViewMode.commands.updateViewMode(readOnly ? 'view' : 'edit'))
  //   editorApi.core?.actions.execute(
  //     editorApi.editorViewMode.commands.updateContentMode({
  //       type: 'switch-content-mode',
  //       contentMode: readOnly ? 'live-view' : 'live-edit'
  //     })
  //   )
  // }, [editorApi, readOnly])

  const onChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.target.value

    // Create ADF structure with plain text
    const formattedAdf: JSONDocNode = {
      version: 1,
      type: 'doc',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: text
            }
          ]
        }
      ]
    }

    if (handleChange) {
      handleChange(formattedAdf)
    }
  }

  const formattedValue = useMemo(() => {
    return {
      version: 1,
      type: 'doc',
      content: filterMediaNodes(value.content)
    }
  }, [value])

  const extractedText = useMemo(() => {
    const textArray = formattedValue.content.flatMap(node => extractTextFromADF(node))
    return readOnly ? textArray.join('<br />') : textArray.join('\n')
  }, [formattedValue])
  return (
    <div
      ref={editorContainerRef}
      style={{
        border: readOnly ? 'none' : '1px solid #ccc',
        height: readOnly ? 'auto' : '100%',
        overflow: 'visible',
        position: 'relative',
        zIndex: 0,
        color: readOnly ? '#6E6E6E' : '',
        fontWeight: readOnly ? '400' : '500'
      }}
      className='w-full'
    >
      <style>
        {`
        div[role="status"].assistive {
          position: absolute !important;
          height: 1px;
          width: 1px;
          overflow: hidden;
          clip: rect(1px, 1px, 1px, 1px);
          white-space: nowrap;
        }
        

        /* Optional: Style the track and thumb if needed to ensure they are visible */
        /* You might need to add these if hiding the buttons affects their visibility */
        ::-webkit-scrollbar {
          width: 6px; /* Adjust as needed */
        }

        ::-webkit-scrollbar-thumb {
          background: #888; /* Adjust as needed */
          border-radius: 4px; /* Adjust as needed */
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #6e6e6e; /* Adjust as needed */
        }
  `}
      </style>
      {readOnly ? (
        <div
          className={`p-2 ${readOnly ? 'text-coal-light' : 'text-coal-dark'} font-normal w-full`}
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {extractedText}
        </div>
      ) : (
        <textarea
          disabled={readOnly}
          value={extractedText ?? ''}
          onChange={onChangeHandler}
          placeholder='Your feedback matters...'
          className={`p-2 ${
            readOnly ? 'text-coal-light' : 'text-coal-dark'
          }  font-normal appearance-none w-full h-full outline-none user-select-none resize-none`}
          style={{
            lineHeight: '1.3rem'
          }}
        ></textarea>
      )}

      {/* <EditorContext>
        <IntlProvider locale='en'>
          <ComposableEditor
            preset={preset}
            defaultValue={formattedValue}
            onChange={onChangeHandler}
            __livePage={true}
            disabled={readOnly}
            appearance={readOnly ? 'chromeless' : 'full-page'}
          />
        </IntlProvider>
      </EditorContext> */}
    </div>
  )
}

// export default ComposableEditorPage
